import React from "react";
import { Pagination } from 'react-bootstrap';
import CsLineIcons from "../../cs-line-icons";

const PAGE_WINDOW_SIZE = 7;
const PAGE_WINDOW_RADIUS = 3;
const PAGE_JUMP_SIZE = 7;

class FeraPagination extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            index: this.props.index || 0
        }
    }

    clampPage = (pageNumber) => {
        const count = Number(this.props.count || 0);
        const next = Number(pageNumber);
        if (!Number.isFinite(next) || count < 1) return 0;
        return Math.min(Math.max(Math.floor(next), 0), count - 1);
    }

    onGotoPage = (pageNumber) => {
        this.setState({
            index: this.clampPage(pageNumber)
        }, this.afterGotoPage);
    }

    afterGotoPage = () => {
        if (this.props.onChange) this.props.onChange(this.state.index); 
    }
    
    componentDidUpdate = (oldProps) => {
        if (this.props.index !== oldProps.index || this.props.count !== oldProps.count) {
            const { index } = this.props;

            this.setState({
                index: this.clampPage(index)
            });
        }
    }

    get pageCount() {
        const count = Number(this.props.count || 0);
        return Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
    }

    get pageIndex() {
        return this.clampPage(this.state.index);
    }

    get atEnd() {
        return this.pageIndex >= (this.pageCount - 1);
    }

    get atStart() {
        return this.pageIndex === 0;
    }

    get visiblePages() {
        const count = this.pageCount;
        if (count <= PAGE_WINDOW_SIZE) return [...Array(count)].map((_, i) => i);

        let start = Math.max(0, this.pageIndex - PAGE_WINDOW_RADIUS);
        let end = Math.min(count - 1, this.pageIndex + PAGE_WINDOW_RADIUS);

        if ((end - start + 1) < PAGE_WINDOW_SIZE) {
            if (start === 0) end = Math.min(count - 1, PAGE_WINDOW_SIZE - 1);
            if (end === count - 1) start = Math.max(0, count - PAGE_WINDOW_SIZE);
        }

        return [...Array(end - start + 1)].map((_, i) => start + i);
    }

    render() {
        if (!this.pageCount || this.pageCount <= 1) return <></>;

        const visiblePages = this.visiblePages;
        const firstVisiblePage = visiblePages[0];
        const lastVisiblePage = visiblePages[visiblePages.length - 1];
        const hasHiddenBefore = firstVisiblePage > 0;
        const hasHiddenAfter = lastVisiblePage < this.pageCount - 1;

        return (
            <Pagination className="justify-content-center flex-wrap mb-0 mt-3">
                <Pagination.First className="shadow" onClick={() => this.onGotoPage(0)} disabled={this.atStart} aria-label="First page" title="First page">
                    <CsLineIcons icon="arrow-double-left" />
                </Pagination.First>
                <Pagination.Prev className="shadow" disabled={this.atStart} onClick={() => this.onGotoPage(this.pageIndex - 1)} aria-label="Previous page" title="Previous page">
                    <CsLineIcons icon="chevron-left" />
                </Pagination.Prev>

                {hasHiddenBefore ? (
                    <Pagination.Item
                        className="shadow"
                        onClick={() => this.onGotoPage(this.pageIndex - PAGE_JUMP_SIZE)}
                        aria-label={`Jump back ${PAGE_JUMP_SIZE} pages`}
                        title={`Jump back ${PAGE_JUMP_SIZE} pages`}
                    >
                        ...
                    </Pagination.Item>
                ) : null}

                {visiblePages.map((i) => (
                    <Pagination.Item key={`pagination${i}`} className="shadow" active={this.pageIndex === i} onClick={() => this.onGotoPage(i)}>
                        {i + 1}
                    </Pagination.Item>
                ))}

                {hasHiddenAfter ? (
                    <Pagination.Item
                        className="shadow"
                        onClick={() => this.onGotoPage(this.pageIndex + PAGE_JUMP_SIZE)}
                        aria-label={`Jump forward ${PAGE_JUMP_SIZE} pages`}
                        title={`Jump forward ${PAGE_JUMP_SIZE} pages`}
                    >
                        ...
                    </Pagination.Item>
                ) : null}

                <Pagination.Next className="shadow" onClick={() => this.onGotoPage(this.pageIndex + 1)} disabled={this.atEnd} aria-label="Next page" title="Next page">
                    <CsLineIcons icon="chevron-right" />
                </Pagination.Next>
                <Pagination.Last className="shadow" onClick={() => this.onGotoPage(this.pageCount - 1)} disabled={this.atEnd} aria-label="Last page" title="Last page">
                    <CsLineIcons icon="arrow-double-right" />
                </Pagination.Last>
            </Pagination>
        )
    }
}

export default FeraPagination;
